// ===================================================================
// HMGP — Subapplications
// Mitigation proposal intake, state review, and FEMA forwarding
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Textarea, Modal, SimpleGrid, ActionIcon, Loader, Center, Table, Tabs,
} from '@mantine/core';
import {
  IconPlus, IconEdit, IconTrash, IconSearch, IconRefresh, IconSend,
  IconCheck, IconArrowBack, IconRocket,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useHMGPStore } from '../../../store/hmgpStore';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useActiveDisaster } from '../../../hooks/useActiveDisaster';
import { useAuthStore } from '../../../store/authStore';
import {
  HMGP_PROJECT_TYPE_LABELS, HMGP_PROJECT_TYPE_COLORS,
  HMGP_SUBAPP_STATUS_LABELS,
  type HMGPProjectType, type HMGPSubapplication, type HMGPSubappStatus,
} from '../../../types/pagrants';
import { fmtUSD as fmt, formatCurrency, stripCurrency } from '../../../lib/formatUtils';

const PROJECT_TYPE_OPTIONS = Object.entries(HMGP_PROJECT_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));
const STATUS_OPTIONS = Object.entries(HMGP_SUBAPP_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }));

const SUBAPP_STATUS_COLORS: Record<string, string> = {
  draft: 'gray', submitted: 'blue', under_review: 'yellow',
  prioritized: 'teal', forwarded_to_fema: 'green', returned: 'orange', withdrawn: 'red',
};

const BCA_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'not_required', label: 'Not Required' },
];

export default function HMGPSubapplications() {
  const navigate = useNavigate();
  const activeDisaster = useActiveDisaster();
  const disasterId = activeDisaster ? String(activeDisaster.Rid) : '';
  const user = useAuthStore(s => s.user);
  const organizations = usePAGrantsStore(s => s.organizations);
  const fetchOrgs = usePAGrantsStore(s => s.fetchOrganizations);

  const {
    projects, subapplications, loadingSubs, fetchSubapplications,
    createSubapplication, updateSubapplication, deleteSubapplication,
    createProject,
  } = useHMGPStore();

  useEffect(() => { fetchSubapplications(); fetchOrgs(); }, []);

  // ── Local state ──────────────────────────────────────────��─
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<HMGPSubapplication | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HMGPSubapplication | null>(null);
  const [detailTarget, setDetailTarget] = useState<HMGPSubapplication | null>(null);

  const [form, setForm] = useState({
    subappTitle: '', projectType: '', organizationId: '',
    problemStatement: '', proposedActivity: '',
    estimatedCost: '', federalRequest: '', nonFederalMatch: '', matchSource: '',
    projectAddress: '', projectCity: '', projectCounty: '', projectState: '',
    bcaStatus: 'not_started', bcaRatio: '',
    statePriorityScore: '', statePriorityRank: '',
    notes: '',
  });

  const filtered = useMemo(() => {
    let list = disasterId ? subapplications.filter(s => s.disasterId === disasterId) : [];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        (s.subappTitle || '').toLowerCase().includes(q) ||
        (s.subappNumber || '').toLowerCase().includes(q)
      );
    }
    if (filterType) list = list.filter(s => s.projectType === filterType);
    if (filterStatus) list = list.filter(s => s.status === filterStatus);
    return list;
  }, [subapplications, disasterId, search, filterType, filterStatus]);

  const orgOptions = organizations.map(o => ({ value: String(o.Rid), label: o.orgName || `Org ${o.Rid}` }));

  function openCreate() {
    setEditTarget(null);
    setForm({
      subappTitle: '', projectType: '', organizationId: '',
      problemStatement: '', proposedActivity: '',
      estimatedCost: '', federalRequest: '', nonFederalMatch: '', matchSource: '',
      projectAddress: '', projectCity: '', projectCounty: '', projectState: '',
      bcaStatus: 'not_started', bcaRatio: '',
      statePriorityScore: '', statePriorityRank: '',
      notes: '',
    });
    setShowModal(true);
  }

  function openEdit(s: HMGPSubapplication) {
    setEditTarget(s);
    setForm({
      subappTitle: s.subappTitle,
      projectType: s.projectType,
      organizationId: s.organizationId,
      problemStatement: s.problemStatement,
      proposedActivity: s.proposedActivity,
      estimatedCost: s.estimatedCost,
      federalRequest: s.federalRequest,
      nonFederalMatch: s.nonFederalMatch,
      matchSource: s.matchSource,
      projectAddress: s.projectAddress,
      projectCity: s.projectCity,
      projectCounty: s.projectCounty,
      projectState: s.projectState,
      bcaStatus: s.bcaStatus || 'not_started',
      bcaRatio: s.bcaRatio,
      statePriorityScore: s.statePriorityScore,
      statePriorityRank: s.statePriorityRank,
      notes: s.notes,
    });
    setShowModal(true);
  }

  async function handleSave() {
    if (!form.subappTitle.trim()) {
      notifications.show({ title: 'Required', message: 'Subapplication title is required', color: 'red' });
      return;
    }
    if (editTarget) {
      await updateSubapplication(editTarget.Rid, { ...form });
      notifications.show({ title: 'Updated', message: 'Subapplication updated', color: 'green' });
    } else {
      await createSubapplication({ ...form, disasterId, creatorId: user?.email || '' });
      notifications.show({ title: 'Created', message: 'Subapplication created', color: 'green' });
    }
    setShowModal(false);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await deleteSubapplication(deleteTarget.Rid);
    notifications.show({ title: 'Deleted', message: 'Subapplication deleted', color: 'red' });
    setDeleteTarget(null);
  }

  async function handleStatusChange(sub: HMGPSubapplication, newStatus: string) {
    await updateSubapplication(sub.Rid, { status: newStatus as any });
    notifications.show({
      title: 'Status Updated',
      message: `${sub.subappNumber} → ${HMGP_SUBAPP_STATUS_LABELS[newStatus as HMGPSubappStatus] || newStatus}`,
      color: 'blue',
    });
    setDetailTarget(null);
  }

  if (!activeDisaster) {
    return (
      <Stack gap="md">
        <Title order={3}>HMGP Subapplications</Title>
        <Paper p="lg" radius="md" style={{ background: '#f1f3f5', textAlign: 'center' }}>
          <Text size="lg" fw={600} c="dimmed" mb="sm">No Active Disaster</Text>
          <Text size="sm" c="dimmed">Select an incident to manage subapplications.</Text>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={3}>HMGP Subapplications</Title>
          <Text size="sm" c="dimmed">Mitigation proposals for {activeDisaster.disasterName}</Text>
        </div>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={fetchSubapplications}>Refresh</Button>
          <Button size="xs" color="green" leftSection={<IconPlus size={14} />} onClick={openCreate}>New Subapplication</Button>
        </Group>
      </Group>

      {/* Status summary */}
      <Group gap="xs">
        {['draft', 'submitted', 'under_review', 'prioritized', 'forwarded_to_fema', 'returned'].map(st => {
          const count = filtered.filter(s => s.status === st).length;
          if (count === 0) return null;
          return (
            <Badge key={st} variant="light" color={SUBAPP_STATUS_COLORS[st] || 'gray'}
              style={{ cursor: 'pointer' }}
              onClick={() => setFilterStatus(filterStatus === st ? null : st)}>
              {HMGP_SUBAPP_STATUS_LABELS[st as HMGPSubappStatus]} ({count})
            </Badge>
          );
        })}
      </Group>

      {/* Filters */}
      <Group gap="xs">
        <TextInput
          placeholder="Search..."
          leftSection={<IconSearch size={14} />}
          size="xs" w={250}
          value={search} onChange={e => setSearch(e.currentTarget.value)}
        />
        <Select size="xs" placeholder="Mitigation Type" data={PROJECT_TYPE_OPTIONS} value={filterType} onChange={setFilterType} clearable w={200} />
        <Select size="xs" placeholder="Status" data={STATUS_OPTIONS} value={filterStatus} onChange={setFilterStatus} clearable w={180} />
        {(search || filterType || filterStatus) && (
          <Button size="xs" variant="subtle" onClick={() => { setSearch(''); setFilterType(null); setFilterStatus(null); }}>Clear</Button>
        )}
      </Group>

      {loadingSubs ? <Center h={200}><Loader /></Center> : filtered.length === 0 ? (
        <Paper p="lg" radius="md" style={{ background: '#f1f3f5', textAlign: 'center' }}>
          <Text c="dimmed">No subapplications found. Create one to start the HMGP process.</Text>
        </Paper>
      ) : (
        <Paper withBorder radius="md" style={{ overflow: 'auto' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>SA #</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Applicant</Table.Th>
                <Table.Th>Est. Cost</Table.Th>
                <Table.Th>BCA</Table.Th>
                <Table.Th>Priority</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th w={80}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map(sub => {
                const org = organizations.find(o => String(o.Rid) === sub.organizationId);
                const typeColor = HMGP_PROJECT_TYPE_COLORS[sub.projectType as HMGPProjectType] || '#6B7280';
                return (
                  <Table.Tr key={sub.Rid} style={{ cursor: 'pointer' }} onClick={() => setDetailTarget(sub)}>
                    <Table.Td><Text size="sm" fw={500}>{sub.subappNumber || '---'}</Text></Table.Td>
                    <Table.Td><Text size="sm">{sub.subappTitle || '---'}</Text></Table.Td>
                    <Table.Td>
                      <Badge size="sm" style={{ backgroundColor: typeColor, color: '#fff' }}>
                        {HMGP_PROJECT_TYPE_LABELS[sub.projectType as HMGPProjectType] || sub.projectType || '---'}
                      </Badge>
                    </Table.Td>
                    <Table.Td><Text size="sm">{org?.orgName || '---'}</Text></Table.Td>
                    <Table.Td><Text size="sm">{sub.estimatedCost ? fmt(parseFloat(sub.estimatedCost) || 0) : '---'}</Text></Table.Td>
                    <Table.Td>
                      {sub.bcaRatio ? (
                        <Badge size="sm" color={parseFloat(sub.bcaRatio) >= 1 ? 'green' : 'red'}>
                          {parseFloat(sub.bcaRatio).toFixed(2)}
                        </Badge>
                      ) : (
                        <Badge size="sm" variant="light" color="gray">{sub.bcaStatus === 'completed' ? '---' : sub.bcaStatus?.replace('_', ' ') || 'N/A'}</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {sub.statePriorityRank ? (
                        <Badge size="sm" variant="light" color="violet">#{sub.statePriorityRank}</Badge>
                      ) : <Text size="sm" c="dimmed">---</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={SUBAPP_STATUS_COLORS[sub.status] || 'gray'}>
                        {HMGP_SUBAPP_STATUS_LABELS[sub.status as HMGPSubappStatus] || sub.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <ActionIcon variant="subtle" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(sub); }}>
                          <IconEdit size={14} />
                        </ActionIcon>
                        <ActionIcon variant="subtle" size="sm" color="red" onClick={(e) => { e.stopPropagation(); setDeleteTarget(sub); }}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* ── Detail / Review Modal ─────────────────────────────── */}
      <Modal opened={!!detailTarget} onClose={() => setDetailTarget(null)} title={detailTarget?.subappNumber || 'Subapplication'} size="lg">
        {detailTarget && (() => {
          const org = organizations.find(o => String(o.Rid) === detailTarget.organizationId);
          return (
            <Stack gap="md">
              <SimpleGrid cols={2}>
                <div><Text size="xs" c="dimmed">Title</Text><Text size="sm" fw={500}>{detailTarget.subappTitle}</Text></div>
                <div><Text size="xs" c="dimmed">Applicant</Text><Text size="sm">{org?.orgName || '---'}</Text></div>
                <div>
                  <Text size="xs" c="dimmed">Mitigation Type</Text>
                  <Badge size="sm" style={{ backgroundColor: HMGP_PROJECT_TYPE_COLORS[detailTarget.projectType as HMGPProjectType] || '#6B7280', color: '#fff' }}>
                    {HMGP_PROJECT_TYPE_LABELS[detailTarget.projectType as HMGPProjectType] || detailTarget.projectType}
                  </Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Status</Text>
                  <Badge color={SUBAPP_STATUS_COLORS[detailTarget.status] || 'gray'}>
                    {HMGP_SUBAPP_STATUS_LABELS[detailTarget.status as HMGPSubappStatus] || detailTarget.status}
                  </Badge>
                </div>
              </SimpleGrid>

              <SimpleGrid cols={3}>
                <div><Text size="xs" c="dimmed">Estimated Cost</Text><Text size="sm" fw={500}>{detailTarget.estimatedCost ? fmt(parseFloat(detailTarget.estimatedCost) || 0) : '---'}</Text></div>
                <div><Text size="xs" c="dimmed">Federal Request</Text><Text size="sm">{detailTarget.federalRequest ? fmt(parseFloat(detailTarget.federalRequest) || 0) : '---'}</Text></div>
                <div><Text size="xs" c="dimmed">Non-Federal Match</Text><Text size="sm">{detailTarget.nonFederalMatch ? fmt(parseFloat(detailTarget.nonFederalMatch) || 0) : '---'}</Text></div>
              </SimpleGrid>

              {detailTarget.problemStatement && (
                <div><Text size="xs" c="dimmed">Problem Statement</Text><Text size="sm">{detailTarget.problemStatement}</Text></div>
              )}
              {detailTarget.proposedActivity && (
                <div><Text size="xs" c="dimmed">Proposed Activity</Text><Text size="sm">{detailTarget.proposedActivity}</Text></div>
              )}

              <SimpleGrid cols={2}>
                <div>
                  <Text size="xs" c="dimmed">BCA Status</Text>
                  <Text size="sm">{detailTarget.bcaStatus?.replace('_', ' ') || 'Not Started'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">BCA Ratio</Text>
                  <Text size="sm" fw={500} c={detailTarget.bcaRatio && parseFloat(detailTarget.bcaRatio) >= 1 ? 'green' : detailTarget.bcaRatio ? 'red' : undefined}>
                    {detailTarget.bcaRatio ? parseFloat(detailTarget.bcaRatio).toFixed(2) : '---'}
                  </Text>
                </div>
              </SimpleGrid>

              {detailTarget.statePriorityRank && (
                <div>
                  <Text size="xs" c="dimmed">State Priority</Text>
                  <Group gap="xs">
                    <Badge variant="light" color="violet">Rank #{detailTarget.statePriorityRank}</Badge>
                    {detailTarget.statePriorityScore && <Text size="sm">Score: {detailTarget.statePriorityScore}</Text>}
                  </Group>
                </div>
              )}

              {detailTarget.notes && (
                <div><Text size="xs" c="dimmed">Notes</Text><Text size="sm">{detailTarget.notes}</Text></div>
              )}

              {/* Linked project banner */}
              {detailTarget.linkedProjectId && (() => {
                const linked = projects.find(p => String(p.Rid) === detailTarget.linkedProjectId);
                return (
                  <Paper p="sm" radius="sm" style={{ background: '#ecfdf5', border: '1px solid #059669' }}>
                    <Group justify="space-between">
                      <Group gap="xs">
                        <IconRocket size={16} color="#059669" />
                        <Text size="sm" fw={500}>Promoted to Project: {linked?.projectNumber || detailTarget.linkedProjectId}</Text>
                      </Group>
                      <Button size="xs" variant="light" color="green"
                        onClick={() => { setDetailTarget(null); navigate(`/pagrants/hmgp/projects/${detailTarget.linkedProjectId}`); }}>
                        Open Project
                      </Button>
                    </Group>
                  </Paper>
                );
              })()}

              {/* Status action buttons */}
              <Group justify="flex-end" gap="xs" mt="md" style={{ borderTop: '1px solid #e0e0e0', paddingTop: 12 }}>
                {detailTarget.status === 'draft' && (
                  <Button size="xs" leftSection={<IconSend size={14} />} onClick={() => handleStatusChange(detailTarget, 'submitted')}>
                    Submit
                  </Button>
                )}
                {detailTarget.status === 'submitted' && (
                  <Button size="xs" color="yellow" leftSection={<IconSearch size={14} />} onClick={() => handleStatusChange(detailTarget, 'under_review')}>
                    Begin Review
                  </Button>
                )}
                {detailTarget.status === 'under_review' && (
                  <>
                    <Button size="xs" color="teal" leftSection={<IconCheck size={14} />} onClick={() => handleStatusChange(detailTarget, 'prioritized')}>
                      Prioritize
                    </Button>
                    <Button size="xs" color="orange" variant="light" leftSection={<IconArrowBack size={14} />} onClick={() => handleStatusChange(detailTarget, 'returned')}>
                      Return
                    </Button>
                  </>
                )}
                {detailTarget.status === 'prioritized' && (
                  <Button size="xs" color="green" leftSection={<IconSend size={14} />} onClick={() => handleStatusChange(detailTarget, 'forwarded_to_fema')}>
                    Forward to FEMA
                  </Button>
                )}
                {detailTarget.status === 'returned' && (
                  <Button size="xs" leftSection={<IconSend size={14} />} onClick={() => handleStatusChange(detailTarget, 'submitted')}>
                    Resubmit
                  </Button>
                )}
                {/* Promote to project — available when forwarded to FEMA and no project yet */}
                {['forwarded_to_fema'].includes(detailTarget.status) && !detailTarget.linkedProjectId && (
                  <Button size="xs" color="green" variant="filled" leftSection={<IconRocket size={14} />}
                    onClick={async () => {
                      const proj = await createProject({
                        projectTitle: detailTarget.subappTitle,
                        projectType: detailTarget.projectType,
                        organizationId: detailTarget.organizationId,
                        disasterId: detailTarget.disasterId,
                        problemDescription: detailTarget.problemStatement,
                        scopeOfWork: detailTarget.proposedActivity,
                        estimatedCost: detailTarget.estimatedCost,
                        federalSharePct: '75',
                        projectAddress: detailTarget.projectAddress,
                        projectCity: detailTarget.projectCity,
                        projectState: detailTarget.projectState,
                        phase: 'fema_review',
                        status: 'fema_review',
                        notes: `Promoted from subapplication ${detailTarget.subappNumber}`,
                      });
                      if (proj) {
                        await updateSubapplication(detailTarget.Rid, { linkedProjectId: String(proj.Rid) });
                        notifications.show({ title: 'Project Created', message: `${proj.projectNumber} created from ${detailTarget.subappNumber}`, color: 'green' });
                        setDetailTarget(null);
                        navigate(`/pagrants/hmgp/projects/${proj.Rid}`);
                      } else {
                        notifications.show({ title: 'Error', message: 'Failed to create project', color: 'red' });
                      }
                    }}>
                    Create Project
                  </Button>
                )}
                <Button size="xs" variant="light" onClick={() => { setDetailTarget(null); openEdit(detailTarget); }}>
                  Edit
                </Button>
              </Group>
            </Stack>
          );
        })()}
      </Modal>

      {/* ── Create/Edit Modal ─────────────────────────────────── */}
      <Modal opened={showModal} onClose={() => setShowModal(false)} title={editTarget ? 'Edit Subapplication' : 'New Subapplication'} size="lg">
        <Tabs defaultValue="basic">
          <Tabs.List mb="md">
            <Tabs.Tab value="basic">Basic Info</Tabs.Tab>
            <Tabs.Tab value="scope">Problem & Scope</Tabs.Tab>
            <Tabs.Tab value="funding">Funding & BCA</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="basic">
            <Stack gap="sm">
              <TextInput label="Subapplication Title" required value={form.subappTitle} onChange={e => setForm(f => ({ ...f, subappTitle: e.currentTarget.value }))} />
              <SimpleGrid cols={2}>
                <Select label="Mitigation Type" data={PROJECT_TYPE_OPTIONS} value={form.projectType} onChange={v => setForm(f => ({ ...f, projectType: v || '' }))} />
                <Select label="Applicant" data={orgOptions} value={form.organizationId} onChange={v => setForm(f => ({ ...f, organizationId: v || '' }))} searchable />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <TextInput label="Address" value={form.projectAddress} onChange={e => setForm(f => ({ ...f, projectAddress: e.currentTarget.value }))} />
                <TextInput label="City" value={form.projectCity} onChange={e => setForm(f => ({ ...f, projectCity: e.currentTarget.value }))} />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <TextInput label="County" value={form.projectCounty} onChange={e => setForm(f => ({ ...f, projectCounty: e.currentTarget.value }))} />
                <TextInput label="State" value={form.projectState} onChange={e => setForm(f => ({ ...f, projectState: e.currentTarget.value }))} />
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="scope">
            <Stack gap="sm">
              <Textarea label="Problem Statement" rows={4} value={form.problemStatement} onChange={e => setForm(f => ({ ...f, problemStatement: e.currentTarget.value }))}
                description="Describe the hazard, risk, and history of losses" />
              <Textarea label="Proposed Activity" rows={4} value={form.proposedActivity} onChange={e => setForm(f => ({ ...f, proposedActivity: e.currentTarget.value }))}
                description="Describe the mitigation action and how it reduces risk" />
              <Textarea label="Notes" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.currentTarget.value }))} />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="funding">
            <Stack gap="sm">
              <SimpleGrid cols={3}>
                <TextInput label="Estimated Total Cost" value={formatCurrency(form.estimatedCost)}
                  onChange={e => setForm(f => ({ ...f, estimatedCost: stripCurrency(e.currentTarget.value) }))}
                  styles={{ input: { fontFamily: 'monospace' } }} />
                <TextInput label="Federal Request (75%)" value={formatCurrency(form.federalRequest)}
                  onChange={e => setForm(f => ({ ...f, federalRequest: stripCurrency(e.currentTarget.value) }))}
                  styles={{ input: { fontFamily: 'monospace' } }} />
                <TextInput label="Non-Federal Match (25%)" value={formatCurrency(form.nonFederalMatch)}
                  onChange={e => setForm(f => ({ ...f, nonFederalMatch: stripCurrency(e.currentTarget.value) }))}
                  styles={{ input: { fontFamily: 'monospace' } }} />
              </SimpleGrid>
              <TextInput label="Match Source" value={form.matchSource} onChange={e => setForm(f => ({ ...f, matchSource: e.currentTarget.value }))}
                description="e.g., Local funds, state match, in-kind services" />
              <SimpleGrid cols={2}>
                <Select label="BCA Status" data={BCA_STATUS_OPTIONS} value={form.bcaStatus} onChange={v => setForm(f => ({ ...f, bcaStatus: v || 'not_started' }))} />
                <TextInput label="BCA Ratio" value={form.bcaRatio} onChange={e => setForm(f => ({ ...f, bcaRatio: e.currentTarget.value }))}
                  description="Must be >= 1.0 for cost-effectiveness" />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <TextInput label="State Priority Score" value={form.statePriorityScore} onChange={e => setForm(f => ({ ...f, statePriorityScore: e.currentTarget.value }))} />
                <TextInput label="State Priority Rank" value={form.statePriorityRank} onChange={e => setForm(f => ({ ...f, statePriorityRank: e.currentTarget.value }))} />
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="lg">
          <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button color="green" onClick={handleSave}>{editTarget ? 'Save Changes' : 'Create Subapplication'}</Button>
        </Group>
      </Modal>

      {/* ── Delete confirm ────────────────────────────────────── */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Subapplication" size="sm">
        <Text size="sm" mb="md">
          Delete <strong>{deleteTarget?.subappNumber}</strong> — {deleteTarget?.subappTitle}? This cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="light" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="red" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>
    </Stack>
  );
}
